import React, { useState, useEffect } from 'react';
import {
  Card, Table, Select, DatePicker, Button, Row, Col, Statistic,
  Typography, Space, Tag, Alert
} from 'antd';
import {
  BarChart3, PieChart, TrendingUp,
  FileText, Filter
} from 'lucide-react';
import dayjs from 'dayjs';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Project, ProjectRevenueStats, ProjectFinancialSummary,
  ProjectRevenue, RevenueType, RevenueStatus
} from '@/types';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface ReportsState {
  projects: Project[];
  selectedProject: Project | null;
  revenueStats: ProjectRevenueStats | null;
  financialSummary: ProjectFinancialSummary | null;
  revenueRecords: ProjectRevenue[];
  loading: boolean;
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const Reports: React.FC = () => {
  const { user } = useAuth();
  const [state, setState] = useState<ReportsState>({
    projects: [],
    selectedProject: null,
    revenueStats: null,
    financialSummary: null,
    revenueRecords: [],
    loading: false,
    dateRange: null
  });

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (state.selectedProject) {
      loadProjectData();
    }
  }, [state.selectedProject, state.dateRange]);

  const loadProjects = async () => {
    try {
      const projects = await api.getCostAccessibleProjects();
      setState(prev => ({
        ...prev,
        projects,
        selectedProject: projects[0] || null
      }));
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadProjectData = async () => {
    if (!state.selectedProject) return;

    setState(prev => ({ ...prev, loading: true }));

    try {
      const dateParams = state.dateRange ? {
        start_date: state.dateRange[0].format('YYYY-MM-DD'),
        end_date: state.dateRange[1].format('YYYY-MM-DD')
      } : {};

      const [revenueStats, financialSummary, revenueRecords] = await Promise.all([
        api.getProjectRevenueStats(state.selectedProject.id, dateParams),
        api.getProjectFinancialSummary(state.selectedProject.id),
        api.getProjectRevenues({
          project_id: state.selectedProject.id,
          ...dateParams,
          limit: 100
        })
      ]);

      setState(prev => ({
        ...prev,
        revenueStats,
        financialSummary,
        revenueRecords: revenueRecords.revenues,
        loading: false
      }));
    } catch (error) {
      console.error('Failed to load project data:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const getRevenueTypeText = (type: RevenueType) => {
    const typeMap = {
      contract_payment: '合同付款',
      milestone_payment: '里程碑付款',
      additional_service: '增值服务',
      other: '其他'
    };
    return typeMap[type] || type;
  };

  const getRevenueStatusTag = (status: RevenueStatus) => {
    const statusMap = {
      pending: { color: 'orange', text: '待确认' },
      confirmed: { color: 'green', text: '已确认' },
      cancelled: { color: 'red', text: '已取消' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const renderOverallStats = () => {
    if (!state.financialSummary || !state.revenueStats) return null;

    return (
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="已确认收入"
              value={state.revenueStats.confirmed_revenue}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#3f8600' }}
              suffix={
                <div className="text-xs text-gray-500">
                  总计: ¥{state.revenueStats.total_revenue.toLocaleString()}
                </div>
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="项目利润"
              value={state.financialSummary.net_profit}
              precision={2}
              prefix="¥"
              valueStyle={{
                color: state.financialSummary.net_profit >= 0 ? '#3f8600' : '#cf1322'
              }}
              suffix={
                <div className="text-xs text-gray-500">
                  利润率: {state.financialSummary.profit_margin.toFixed(1)}%
                </div>
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="项目成本"
              value={state.financialSummary.total_cost}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#cf1322' }}
              suffix={
                <div className="text-xs text-gray-500">
                  内部: ¥{state.financialSummary.internal_cost.toLocaleString()}
                </div>
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="收入记录"
              value={state.revenueStats.revenue_count}
              suffix={
                <div>
                  <span>条</span>
                  <div className="text-xs text-gray-500">
                    待确认: {state.revenueStats.pending_count}条
                  </div>
                </div>
              }
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const renderRevenueByType = () => {
    if (!state.revenueStats || Object.keys(state.revenueStats.by_type).length === 0) {
      return null;
    }

    return (
      <Card
        title={
          <Space>
            <PieChart />
            收入类型分析
          </Space>
        }
        className="mb-6"
      >
        <Row gutter={16}>
          {Object.entries(state.revenueStats.by_type).map(([type, amount]) => (
            <Col span={6} key={type}>
              <Card size="small">
                <Statistic
                  title={getRevenueTypeText(type as RevenueType)}
                  value={amount}
                  precision={2}
                  prefix="¥"
                  suffix={
                    <div className="text-xs text-gray-500">
                      占比: {((amount / state.revenueStats!.confirmed_revenue) * 100).toFixed(1)}%
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  const renderMonthlyRevenue = () => {
    if (!state.revenueStats || Object.keys(state.revenueStats.by_month).length === 0) {
      return null;
    }

    const monthlyData = Object.entries(state.revenueStats.by_month)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, amount]) => ({
        month: dayjs(month).format('YYYY年MM月'),
        amount
      }));

    return (
      <Card
        title={
          <Space>
            <TrendingUp />
            月度收入趋势
          </Space>
        }
        className="mb-6"
      >
        <Row gutter={16}>
          {monthlyData.map(({ month, amount }) => (
            <Col span={6} key={month}>
              <Card size="small">
                <Statistic
                  title={month}
                  value={amount}
                  precision={2}
                  prefix="¥"
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  const revenueColumns = [
    {
      title: '收入日期',
      dataIndex: 'revenue_date',
      key: 'revenue_date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a: any, b: any) => dayjs(a.revenue_date).unix() - dayjs(b.revenue_date).unix(),
    },
    {
      title: '收入金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `¥${amount.toLocaleString()}`,
      sorter: (a: any, b: any) => a.amount - b.amount,
    },
    {
      title: '收入类型',
      dataIndex: 'revenue_type',
      key: 'revenue_type',
      render: (type: RevenueType) => getRevenueTypeText(type),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: RevenueStatus) => getRevenueStatusTag(status),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>
          <BarChart3 className="inline mr-2" />
          收入分析报表
        </Title>
      </div>

      {/* 筛选条件 */}
      <Card className="mb-6">
        <Row gutter={16} align="middle">
          <Col span={6}>
            <div>
              <div className="mb-2 font-medium">选择项目:</div>
              <Select
                style={{ width: '100%' }}
                placeholder="选择项目"
                value={state.selectedProject?.id}
                onChange={(projectId) => {
                  const project = state.projects.find(p => p.id === projectId);
                  setState(prev => ({ ...prev, selectedProject: project || null }));
                }}
                loading={state.loading}
              >
                {state.projects.map(project => (
                  <Option key={project.id} value={project.id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col span={8}>
            <div>
              <div className="mb-2 font-medium">时间范围:</div>
              <RangePicker
                style={{ width: '100%' }}
                value={state.dateRange}
                onChange={(dates) => setState(prev => ({ ...prev, dateRange: dates }))}
                format="YYYY-MM-DD"
              />
            </div>
          </Col>
          <Col span={4}>
            <div className="mt-6">
              <Button
                type="primary"
                icon={<Filter />}
                onClick={loadProjectData}
                loading={state.loading}
              >
                刷新数据
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {state.selectedProject && (
        <>
          <Alert
            message={`当前分析项目: ${state.selectedProject.name}`}
            type="info"
            showIcon
            className="mb-6"
          />

          {/* 整体统计 */}
          {renderOverallStats()}

          {/* 收入类型分析 */}
          {renderRevenueByType()}

          {/* 月度收入趋势 */}
          {renderMonthlyRevenue()}

          {/* 收入记录详情 */}
          <Card
            title={
              <Space>
                <FileText />
                收入记录明细
              </Space>
            }
          >
            <Table
              columns={revenueColumns}
              dataSource={state.revenueRecords}
              rowKey="id"
              loading={state.loading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
              }}
            />
          </Card>
        </>
      )}

      {!state.selectedProject && state.projects.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <Title level={4} className="text-gray-500">暂无可访问的项目</Title>
            <p className="text-gray-400">请联系管理员获取项目访问权限</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Reports;